'use client';

import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { useRouter } from 'next/navigation';
import { pathForNextNavigation } from '@/lib/appBasePath';

import { Iconify } from '../../iconify';
import { UI_ICONS } from '@/lib/ui-icons';
import { useRBAC } from '@/contexts/RBACContext';
import { useAdminCheck } from '@/hooks/useAdminCheck';
import { useMemo } from 'react';

// ----------------------------------------------------------------------
// Each link requires its permission (RBAC). No permission = link hidden.
// Entire "Tenant" block (heading + card) is hidden if user has none of these,
// or while RBAC is loading (avoids flashing links the user cannot use).
// ----------------------------------------------------------------------

type TenantNavItem =
  | { title: string; path: string; icon: string; permission: string }
  | { title: string; path: string; icon: string; anyPermissions: readonly string[] };

const accountItems: TenantNavItem[] = [
  {
    title: 'Brand Management',
    path: '/account/brands',
    icon: UI_ICONS.brandManagement,
    permission: 'settings.view',
  },
  {
    title: 'Subscription & Usage',
    path: '/account/subscription',
    icon: UI_ICONS.subscription,
    permission: 'subscription.view',
  },
  {
    title: 'Wallet',
    path: '/account/wallet',
    icon: 'solar:wallet-bold',
    permission: 'subscription.view',
  },
  {
    title: 'Team & Access',
    path: '/account/organization',
    icon: UI_ICONS.teamAccess,
    permission: 'member.view',
  },
  {
    title: 'Integrations',
    path: '/integrations',
    icon: UI_ICONS.integrations,
    anyPermissions: ['integration.view', 'integration.edit'],
  },
  {
    title: 'Signal Weights',
    path: '/account/signal-weights',
    icon: 'mdi:pulse',
    permission: 'settings.view',
  },
];

const navCardSx = {
  p: 2,
  borderRadius: '14px',
  bgcolor: (t: { palette: { mode: string } }) =>
    t.palette.mode === 'dark' ? 'background.paper' : 'grey.100',
  border: '1px solid',
  borderColor: (t: { palette: { mode: string } }) =>
    t.palette.mode === 'dark' ? 'grey.600' : 'grey.300',
} as const;

const navItemButtonSx = {
  minHeight: 44,
  borderRadius: 0.75,
  typography: 'body2',
  color: 'text.primary',
  textTransform: 'capitalize',
  fontWeight: 'fontWeightMedium',
  '&:hover': {
    bgcolor: 'action.hover',
  },
} as const;

export function AccountOptions() {
  const router = useRouter();
  const rbac = useRBAC();
  const { isAdmin, loading: adminLoading } = useAdminCheck();

  // Sub-account members only (parent_account_id set). Root-account members use org wallet/subscription.
  const isSubAccountMember = useMemo(() => {
    return (
      Boolean(rbac.userContext) &&
      !rbac.userContext?.is_org_owner &&
      rbac.userContext?.parent_account_id != null
    );
  }, [rbac.userContext]);

  const visibleItems = useMemo(() => {
    if (rbac.isLoading) {
      return [];
    }
    return accountItems
      .filter((item) => {
        if ('anyPermissions' in item) {
          return rbac.hasAnyPermission([...item.anyPermissions]);
        }
        return rbac.hasPermission(item.permission);
      })
      .map((item) => {
        if (isSubAccountMember) {
          if (item.path === '/account/subscription') {
            return { ...item, path: '/account/subscription/sub-account', title: 'Account Subscription' };
          }
          if (item.path === '/account/wallet') {
            return { ...item, path: '/account/wallet/sub-account', title: 'Account Wallet' };
          }
        }
        return item;
      });
  }, [rbac.isLoading, rbac.hasPermission, rbac.hasAnyPermission, rbac.permissions, rbac.userContext, isSubAccountMember]);

  const showTenant = !rbac.isLoading && visibleItems.length > 0;
  const showAdmin = !adminLoading && isAdmin;

  if (!showTenant && !showAdmin) {
    return null;
  }

  const handleNavigation = (path: string) => {
    router.push(pathForNextNavigation(path));
  };

  return (
    <>
      {showTenant && (
        <Box sx={navCardSx}>
          <Typography variant="overline" sx={{ px: 2, color: 'text.disabled', fontSize: 11, display: 'block' }}>
            Tenant
          </Typography>

          <List disablePadding sx={{ mt: 1 }}>
            {visibleItems.map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton onClick={() => handleNavigation(item.path)} sx={navItemButtonSx}>
                  <ListItemIcon sx={{ width: 22, height: 22, mr: 2 }}>
                    <Iconify icon={item.icon} width={20} />
                  </ListItemIcon>
                  <ListItemText primary={item.title} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {showAdmin && (
        <Box sx={navCardSx}>
          <Typography variant="overline" sx={{ px: 2, color: 'text.disabled', fontSize: 11, display: 'block' }}>
            Admin
          </Typography>

          <List disablePadding sx={{ mt: 1 }}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => handleNavigation('/admin')}
                sx={navItemButtonSx}
              >
                <ListItemIcon sx={{ width: 22, height: 22, mr: 2 }}>
                  <Iconify icon="solar:shield-user-bold" width={20} />
                </ListItemIcon>
                <ListItemText primary="Admin panel" />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      )}
    </>
  );
}
